import React, { useState, useEffect, useCallback } from "react";
import { Link } from "react-router-dom";
import Header from "../components/Header";
import Footer from "../components/Footer";
import EventCard from "../components/EventCard";
import { Arrow } from "../components/Icons";
import useReveal from "../utils/useReveal";
import { API_URL, MEMBERSHIP_URL, PROGRAMS_URL } from "../config";
import "../styles/Home.css";

import photoWorship from "../assets/images/church.jpg";
import photoCharagu from "../assets/images/charagu.jpg";
import photoElizabeth from "../assets/images/elizabeth.jpg";
import photoMuna from "../assets/images/muna.jpg";
import photoMakuyu from "../assets/images/makuyu.jpg";

const HERO_STATS = [
    { value: "1980", label: "the year the ministry began" },
    { value: "6", label: "chapters, in Kenya and the Diaspora" },
    { value: "100+", label: "active members" },
    { value: "1,000+", label: "people associated with the work" },
];

// The four photos that overlap beside "The work", front to back.
const COLLAGE = [
    { photo: photoCharagu, alt: "A CFCM member speaking at an outdoor gathering" },
    { photo: photoElizabeth, alt: "A CFCM member speaking at an outdoor gathering" },
    { photo: photoMuna, alt: "A CFCM member reading from the Bible at an outdoor gathering" },
    { photo: photoMakuyu, alt: "The Peniel Prayer and Retreat Center in Makuyu" },
];

const STORIES = [
    {
        tag: "School missions",
        title: "One message of repentance, in school after school",
        text: "Reaching out to young people in schools has been the ministry's approach from the start. Thousands of young people have come to accept Jesus as Lord and Saviour.",
        href: PROGRAMS_URL,
        more: "Read about school missions",
    },
    {
        tag: "Membership",
        title: "The harvest is plenty and the work is increasing",
        text: "Hundreds of students and other members of society receive Christ every year as we preach the Gospel. Join us and reach more people with it.",
        href: MEMBERSHIP_URL,
        more: "Read about membership",
    },
    {
        tag: "Makuyu",
        title: "A quiet place to pray and to retreat",
        text: "The Peniel Prayer and Retreat Center in Makuyu welcomes individuals and groups. Book the dates of your visit here.",
        to: "/makuyu-prayer-center-booking",
        more: "Book your visit",
    },
];

const STEPS = [
    { title: "Create your account", text: "Give your name and email address, and choose a password." },
    { title: "Confirm your email", text: "Open the link we send you. It shows that the email address is yours." },
    { title: "Tell us about yourself", text: "Sign in and add your chapter, your place of worship and a few more details." },
    { title: "Get approved", text: "An administrator checks your details and approves your account. Then the portal is open to you." },
];

function Home() {
    const [events, setEvents] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState("");

    const fetchEvents = useCallback(async () => {
        try {
            const response = await fetch(`${API_URL}/events/public/upcoming`, {
                method: "GET",
                headers: {
                    "Content-Type": "application/json",
                },
            });

            if (response.ok) {
                const data = await response.json();
                setEvents(data.events || []);
            } else {
                throw new Error("Failed to fetch events");
            }
        } catch (error) {
            setError("The upcoming events could not be loaded just now. Please try again later.");
            console.error("Error:", error);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        fetchEvents();
    }, [fetchEvents]);

    // The event cards arrive after the page: look again for things to fade in once they are here.
    useReveal(`${loading}-${events.length}`);

    return (
        <div className="cf-public">
            <Header />

            <main>
                <section className="cf-hero" id="top">
                    <img className="cf-hero-photo" src={photoWorship} alt="" />
                    <div className="cf-hero-shade"></div>

                    <div className="cf-wrap">
                        <div className="cf-hero-text">
                            <h1>Making and developing effective <em>disciples.</em></h1>

                            <p className="cf-lede">
                                At CFCM we aim to make and develop effective disciples in fulfilling Christ's
                                mission by encouraging, equipping, mobilizing and mentoring, using biblical
                                principles as inspired by the Holy Spirit.
                            </p>

                            <div className="cf-cta-row">
                                <Link className="cf-btn cf-btn-accent cf-btn-lg" to="/support-the-ministry">Support the ministry <Arrow /></Link>
                                <Link className="cf-btn cf-btn-outline cf-btn-lg" to="/login">Sign in</Link>
                            </div>
                        </div>

                        <div className="cf-hero-stats">
                            {HERO_STATS.map((stat) => (
                                <div className="cf-hero-stat" key={stat.label}>
                                    <strong>{stat.value}</strong>
                                    <span>{stat.label}</span>
                                </div>
                            ))}
                        </div>
                    </div>
                </section>

                <section className="cf-work">
                    <div className="cf-wrap cf-work-grid">
                        <div className="cf-collage cf-reveal">
                            {COLLAGE.map((item) => (
                                <div className="cf-collage-item" key={item.photo}>
                                    <img src={item.photo} alt={item.alt} loading="lazy" />
                                </div>
                            ))}
                        </div>

                        <div className="cf-work-text">
                            <span className="cf-eyebrow cf-reveal">The work</span>
                            <h2 className="cf-sec-title cf-reveal">Preaching the Gospel, and making disciples who stay</h2>

                            <ul className="cf-stories">
                                {STORIES.map((story, index) => (
                                    <li className="cf-reveal" key={story.tag} style={{ "--cf-delay": `${index * 0.1}s` }}>
                                        <h3>{story.title}</h3>
                                        <p>{story.text}</p>
                                        {story.to
                                            ? <Link className="cf-more" to={story.to}>{story.more} <Arrow /></Link>
                                            : <a className="cf-more" href={story.href} target="_blank" rel="noopener noreferrer">{story.more} <Arrow /></a>}
                                    </li>
                                ))}
                            </ul>

                            <div className="cf-reveal">
                                <Link className="cf-btn cf-btn-light" to="/about-us">Read our story <Arrow /></Link>
                            </div>
                        </div>
                    </div>
                </section>

                <section className="cf-sec" id="events">
                    <div className="cf-wrap">
                        <div className="cf-sec-head cf-reveal">
                            <span className="cf-eyebrow">Upcoming events</span>
                            <h2 className="cf-sec-title">Come and be part of it</h2>
                            <p className="cf-sec-sub">
                                Conferences, reflections and other times when the ministry comes together.
                            </p>
                        </div>

                        {error && <p className="cf-empty cf-reveal">{error}</p>}

                        {!error && loading && <p className="cf-empty">Loading the upcoming events...</p>}

                        {!error && !loading && events.length === 0 && (
                            <p className="cf-empty cf-reveal">No events have been announced yet. Please check again soon.</p>
                        )}

                        {events.length > 0 && (
                            <div className="cf-events">
                                {events.slice(0, 3).map((event, index) => (
                                    <EventCard event={event} key={event.id} className="cf-reveal" style={{ "--cf-delay": `${index * 0.1}s` }} />
                                ))}
                            </div>
                        )}

                        {events.length > 0 && (
                            <div className="cf-more-row cf-reveal">
                                <Link className="cf-btn cf-btn-primary cf-btn-lg" to="/events">See all events <Arrow /></Link>
                            </div>
                        )}
                    </div>
                </section>

                <section className="cf-band">
                    <div className="cf-wrap">
                        <div className="cf-sec-head cf-reveal">
                            <span className="cf-eyebrow">Become a member</span>
                            <h2 className="cf-sec-title">Four steps to join the portal</h2>
                            <p className="cf-sec-sub">It takes a few minutes. Membership is renewed every year.</p>
                        </div>

                        <ol className="cf-steps">
                            {STEPS.map((step, index) => (
                                <li className="cf-step cf-reveal" key={step.title} style={{ "--cf-delay": `${index * 0.08}s` }}>
                                    <div className="cf-step-num">{index + 1}</div>
                                    <h3>{step.title}</h3>
                                    <p>{step.text}</p>
                                </li>
                            ))}
                        </ol>

                        <div className="cf-more-row cf-reveal">
                            <Link className="cf-btn cf-btn-primary cf-btn-lg" to="/signup">Create your account <Arrow /></Link>
                        </div>
                    </div>
                </section>

                <section className="cf-cta-photo">
                    <img src={photoMakuyu} alt="" loading="lazy" />
                    <div className="cf-hero-shade"></div>
                    <div className="cf-wrap cf-reveal" style={{ position: "relative" }}>
                        <h2>The harvest is plenty. <em>Partner with us.</em></h2>
                        <p>Your gift helps us reach more young people with the Gospel of our Lord Jesus Christ.</p>
                        <div className="cf-cta-row is-centered">
                            <Link className="cf-btn cf-btn-accent cf-btn-lg" to="/support-the-ministry">Support the ministry <Arrow /></Link>
                            <Link className="cf-btn cf-btn-outline-light cf-btn-lg" to="/signup">Become a member</Link>
                        </div>
                    </div>
                </section>
            </main>
            <Footer />
        </div>
    );
}

export default Home;
